/**
 * Dates calendaires et créneaux, en TS pur (pas de `Date` locale : tout est
 * calculé en UTC sur des chaînes `YYYY-MM-DD`, pour ne dépendre ni du fuseau
 * de la machine — Vercel tourne en UTC, le navigateur non — ni de l'heure
 * d'été).
 */

export const AI_POLL_TIMEZONE = "Europe/Paris";

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `2026-02-30` et `2026-13-01` sont refusés (une regex seule les laisserait passer). */
export function isRealIsoDate(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || d < 1) return false;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === mo - 1 &&
    dt.getUTCDate() === d
  );
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function fromUtc(dt: Date): string {
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

/** Date calendaire « aujourd'hui » dans un fuseau donné. */
export function todayInTimeZone(
  now: Date,
  timeZone: string = AI_POLL_TIMEZONE,
): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  return fromUtc(new Date(Date.UTC(y, m - 1, d + days)));
}

/** Ajoute des mois en ramenant au dernier jour du mois si besoin (31/08 + 1 → 30/09). */
export function addMonths(isoDate: string, months: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return fromUtc(target);
}

/** `DD/MM/YYYY` (ou `D/M/YYYY`, séparateurs `/` `.` `-`) → ISO, sinon `null`. */
export function frenchDateToIso(value: string): string | null {
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(value.trim());
  if (!m) return null;
  const iso = `${m[3]}-${pad2(Number(m[2]))}-${pad2(Number(m[1]))}`;
  return isRealIsoDate(iso) ? iso : null;
}

// ---------------------------------------------------------------------------
// Créneaux
// ---------------------------------------------------------------------------

/** Créneau validé, heures locales (« mur »), `start` < `end`, même jour. */
export interface TimeRange {
  start: string; // HH:MM
  end: string; // HH:MM
}

const STRICT_RANGE = /^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)$/;
// Mode souple : `9:00-12:00`, `19h-22h`, `19h30 – 22h`, espaces, tirets typographiques.
const LENIENT_RANGE =
  /^(\d{1,2})(?:[:h](\d{2})?)?-(\d{1,2})(?:[:h](\d{2})?)?$/i;

export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function fmt(h: number, m: number): string {
  return `${pad2(h)}:${pad2(m)}`;
}

/**
 * `HH:MM-HH:MM` → `TimeRange`, ou `null` si illisible, hors 00:00–23:59, ou
 * si la fin n'est pas strictement après le début (pas de créneau à cheval sur
 * minuit).
 */
export function parseTimeRange(
  raw: unknown,
  mode: "strict" | "lenient" = "strict",
): TimeRange | null {
  if (typeof raw !== "string") return null;
  let h1: number, m1: number, h2: number, m2: number;
  if (mode === "strict") {
    const m = STRICT_RANGE.exec(raw.trim());
    if (!m) return null;
    [h1, m1, h2, m2] = [m[1], m[2], m[3], m[4]].map(Number);
  } else {
    const compact = raw.replace(/[–—−]/g, "-").replace(/\s+/g, "");
    const m = LENIENT_RANGE.exec(compact);
    if (!m) return null;
    h1 = Number(m[1]);
    m1 = Number(m[2] ?? 0);
    h2 = Number(m[3]);
    m2 = Number(m[4] ?? 0);
    // `19-22` sans séparateur d'heure est ambigu avec une plage de dates :
    // on n'accepte l'absence de minutes qu'avec un « h » explicite.
    if (!/[:h]/i.test(compact)) return null;
  }
  if (h1 > 23 || h2 > 23 || m1 > 59 || m2 > 59) return null;
  if (h2 * 60 + m2 <= h1 * 60 + m1) return null;
  return { start: fmt(h1, m1), end: fmt(h2, m2) };
}

/** Dédoublonne et trie par heure de début puis de fin. */
export function normalizeRanges(ranges: TimeRange[]): TimeRange[] {
  const seen = new Map<string, TimeRange>();
  for (const r of ranges) seen.set(`${r.start}-${r.end}`, r);
  return [...seen.values()].sort(
    (a, b) =>
      toMinutes(a.start) - toMinutes(b.start) ||
      toMinutes(a.end) - toMinutes(b.end),
  );
}

/** Vrai si deux créneaux (triés) se chevauchent. Des créneaux bout à bout ne se chevauchent pas. */
export function hasOverlap(sorted: TimeRange[]): boolean {
  for (let i = 1; i < sorted.length; i++) {
    if (toMinutes(sorted[i].start) < toMinutes(sorted[i - 1].end)) return true;
  }
  return false;
}
